import { createBrowserClient } from "@supabase/ssr";

import type { Database } from "./types";

const isBrowser = typeof window !== "undefined";

// AIDEV-NOTE: Support runtime env vars via window.env for deployment flexibility
export const supabaseUrl =
    (isBrowser && window.env?.SUPABASE_URL) || process.env.SUPABASE_URL;

export const supabaseKey =
    (isBrowser && window.env?.SUPABASE_ANON_PUBLIC) ||
    process.env.SUPABASE_ANON_PUBLIC;

if (!supabaseUrl) {
    throw new Error("Missing Supabase URL environment variable");
}
if (!supabaseKey) {
    throw new Error("Missing Supabase Key environment variable");
}

function isStorageWritable() {
    try {
        localStorage.setItem("__t", "1");
        localStorage.removeItem("__t");
        return true;
    } catch {
        return false;
    }
}

// In-memory fallback that fully implements the Storage interface
function createMemoryStorage(): Storage {
    let store: Record<string, string> = {};
    return {
        get length() {
            return Object.keys(store).length;
        },
        clear() {
            store = {};
        },
        getItem(key: string) {
            return store.hasOwnProperty(key) ? store[key] : null;
        },
        key(index: number) {
            return Object.keys(store)[index] ?? null;
        },
        removeItem(key: string) {
            delete store[key];
        },
        setItem(key: string, value: string) {
            store[key] = value;
        },
    } as Storage;
}

const isWritable = isStorageWritable();
const safeStorage = isWritable ? localStorage : createMemoryStorage();

// Monkey-patch the real localStorage so that any *other* modules that haven’t
// been updated yet won’t crash when they call setItem in Private-Browsing.
if (!isWritable) {
    try {
        const noop = () => {};
        localStorage.setItem = noop as any;
        localStorage.removeItem = noop as any;
        localStorage.clear = noop as any;
    } catch {
        /* ignore – cannot redefine, but that’s fine */
    }
}

export const supabase = createBrowserClient(supabaseUrl, supabaseKey, {
    auth: {
        storage: safeStorage,
        persistSession: isWritable,
    },
});
// AIDEV-NOTE: SSR-enabled browser client that syncs with server cookies
